import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { DiktatFeld, VorschlagsKasten } from '@symphenia/ui-components'
import { BESPRECHUNG_KUERZEL, besprechungAusDiktat, siteDocDiktatMax, type BesprechungDiktatPunkt, type BesprechungInhalt, type SiteDocAiProposal, type SiteDocAiProposalItem, type SiteDocDetail } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { siteDocAiProposal, updateSiteDoc } from '../../api/site-docs.api'
import { punktAnlegen, tagesordnungSetzen } from './api'

type Eintrag = SiteDocAiProposalItem<BesprechungDiktatPunkt>

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const an = () => setOnline(true)
    const aus = () => setOnline(false)
    window.addEventListener('online', an)
    window.addEventListener('offline', aus)
    return () => {
      window.removeEventListener('online', an)
      window.removeEventListener('offline', aus)
    }
  }, [])
  return online
}

/**
 * AP-49 13. im ERP — **Mitschrift in Punkte** am Entwurf: das Feld (gespeichert als `dictation` am Entwurf,
 * höchstens 12.000 Zeichen, RN-8), „Vorschlag aus dem Text" und der Vorschlagskasten. Übernehmen legt neue
 * Tagesordnungspunkte an (nach Titel) und die Punkte dahinter — Zuständige im Freitext werden gegen die
 * Teilnehmer zugeordnet, Aufgaben beginnen „offen"; übernommene Punkte bleiben, wie sie sind.
 */
export function BesprechungDiktat({ dok, inhalt, onGeaendert }: { dok: SiteDocDetail; inhalt: BesprechungInhalt; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const online = useOnline()
  const daten = useMemo<Datensatz>(() => ({ dictation: dok.dictation ?? '' }), [dok.dictation])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `besprechungDiktat:${dok.id}`,
    bezeichnung: t('siteDocs.meeting.dictation'),
    daten,
    schreibe: async (patch) => {
      const neu = await updateSiteDoc(dok.kind, dok.id, { dictation: (patch.dictation as string | null) || null })
      onGeaendert(neu)
      return { dictation: neu.dictation ?? '' }
    },
  }) as Speicherstelle<unknown>
  const feld = useFeld<string | null>(stelle, 'dictation', { label: t('siteDocs.meeting.dictation') })
  const [vorschlag, setVorschlag] = useState<SiteDocAiProposal | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const eintraege = useMemo(() => {
    if (!vorschlag) return [] as Eintrag[]
    const bekannt = new Set(inhalt.tagesordnung.map((x) => x.titel.trim().toLocaleLowerCase('de')))
    return (vorschlag.items as unknown as Eintrag[]).map((e) => ({ ...e, daten: { ...e.daten, topNeu: !!e.daten.top && !bekannt.has(e.daten.top.trim().toLocaleLowerCase('de')) } }))
  }, [vorschlag, inhalt.tagesordnung])

  const holen = async () => {
    setFehler(null)
    try {
      setVorschlag(await siteDocAiProposal({ kind: 'MEETING', text: feld.props.value, projectId: dok.projectId, projectRefId: dok.projectRefId, documentDate: dok.documentDate.slice(0, 10) }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const uebernehmen = async (liste: Eintrag[]) => {
    setFehler(null)
    const r = besprechungAusDiktat(inhalt, liste.map((e) => e.daten), () => crypto.randomUUID())
    let letzt: SiteDocDetail | null = null
    try {
      if (r.neueTops.length) letzt = await tagesordnungSetzen(dok.id, r.tagesordnung)
      for (const p of r.punkte) {
        letzt = await punktAnlegen(dok.id, {
          kind: p.kind,
          text: p.text,
          agendaKey: p.agendaKey,
          ...(p.kind === 'TASK' ? { assigneeName: p.assigneeName, assigneeCompany: p.assigneeCompany, assigneeUserId: p.assigneeUserId, dueDate: p.dueDate, dueText: p.dueText, state: 'OPEN' } : {}),
        })
      }
    } catch (e) {
      setFehler(fehlertext(e, t))
      throw e
    } finally {
      if (letzt) onGeaendert(letzt)
    }
  }

  const darstellung = (e: Eintrag) => {
    const d = e.daten
    return (
      <span>
        <span className="font-medium">{BESPRECHUNG_KUERZEL[d.kind]}</span> {d.text}
        <span className="ml-2 text-muted-foreground">
          {[d.top && (d.topNeu ? t('siteDocs.meeting.newAgendaItemHint', { titel: d.top }) : d.top), d.assigneeName, d.dueDate ? d.dueDate.split('-').reverse().join('.') : d.dueText].filter(Boolean).join(' · ')}
        </span>
      </span>
    )
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <DiktatFeld
        wert={feld.props.value}
        onChange={(v) => feld.props.onChange({ target: { value: v } } as ChangeEvent<HTMLTextAreaElement>)}
        onBlur={feld.props.onBlur}
        onVorschlag={holen}
        online={online}
        fehler={fehler}
        max={siteDocDiktatMax('MEETING')}
        texte={{ titel: t('siteDocs.meeting.dictation'), tastatur: t('siteDocs.diktat.tastatur'), knopf: t('siteDocs.diktat.knopf'), arbeitet: t('siteDocs.diktat.arbeitet'), ohneNetz: t('siteDocs.diktat.ohneNetz'), kiHinweis: t('siteDocs.diktat.kiHinweis') }}
      />
      {vorschlag && (
        <>
          {!!vorschlag.hinweise?.length && (
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-800 dark:text-amber-200">
              {vorschlag.hinweise.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          )}
          <VorschlagsKasten
            eintraege={eintraege}
            darstellung={darstellung}
            onUebernehmen={uebernehmen}
            onVerwerfen={() => setVorschlag(null)}
            texte={{
              titel: t('siteDocs.diktat.vorschlag'),
              uebernehmen: t('siteDocs.diktat.uebernehmen'),
              markierte: t('siteDocs.diktat.markierte'),
              verwerfen: t('siteDocs.diktat.verwerfen'),
              leer: t('siteDocs.diktat.leer'),
              hinweis: (c) => t(`siteDocs.diktat.hints.${c}`, c),
            }}
          />
        </>
      )}
    </div>
  )
}
